import React, { useEffect, useRef, useState } from "react";

export default function VoiceAgent() {
  const [connected, setConnected] = useState(false);
  const [logs, setLogs] = useState([]);
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState("");
  const pcRef = useRef(null);
  const dcRef = useRef(null);
  const micStreamRef = useRef(null);

  const log = (msg, obj) => {
    setLogs((l) => [...l.slice(-200), `[${new Date().toLocaleTimeString()}] ${msg}`]);
    if (obj) console.debug(msg, obj);
  };

  const tools = {
    askSuperviserAgent: async ({ caseDetails }) => {
      log(`Агент сформулировал вопрос: ${caseDetails}`);
      setQuestion(caseDetails);

      try {
        const r = await fetch("https://botlegal.tech/supervisor", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ caseDetails }),
        });

        if (!r.ok) throw new Error(`Ошибка запроса: ${r.status}`);
        const data = await r.json();
        console.log(data.text)

        const reply = data?.answer || data?.result || JSON.stringify(data.text);
        log(`Ответ SupervisorAgent: ${reply}`);
        setAnswer(reply);

        return { success: true, caseDetails, reply };
      } catch (err) {
        log("Ошибка при обращении к SupervisorAgent", err);
        return { success: false, error: err.message };
      }
    },
  };

  async function startRelay() {
    try {
      log("Запрашиваю микрофон...");
      const mic = await navigator.mediaDevices.getUserMedia({ audio: true });
      micStreamRef.current = mic;

      const pc = new RTCPeerConnection();
      pcRef.current = pc;

      mic.getTracks().forEach((track) => pc.addTrack(track, mic));

      const audioEl = document.createElement("audio");
      audioEl.autoplay = true;
      pc.ontrack = (ev) => {
        audioEl.srcObject = ev.streams[0];
        log("Получен аудиопоток от сервера");
      };

      const dc = pc.createDataChannel("oai-events");
      dcRef.current = dc;

      dc.onopen = () => {
        log("DataChannel открыт — отправляю session.update");

        const sessionUpdate = {
          type: "session.update",
          session: {
            instructions: `
              Ты — юридический голосовой ассистент по налоговым вопросам Россий .
              1) Уточняй суть вопроса пользователя.
              2) Сформулируй итоговый юридический вопрос.
              3) Когда вопрос готов, вызови функцию askSuperviserAgent с параметром caseDetails.
              4) Не озвучивай результат — просто вызови функцию.
            `,
            modalities: ["audio", "text"],
            tools: [
              {
                type: "function",
                name: "askSuperviserAgent",
                description: "Передаёт сформулированный вопрос в SupervisorAgent и получает ответ",
                parameters: {
                  type: "object",
                  properties: {
                    caseDetails: {
                      type: "string",
                      description: "Итоговая формулировка вопроса",
                    },
                  },
                  required: ["caseDetails"],
                },
              },
            ],
          },
        };

        dc.send(JSON.stringify(sessionUpdate));
      };

      dc.onmessage = async (ev) => {
        try {
          const msg = JSON.parse(ev.data);
          if (msg.type === "response.function_call_arguments.done") {
            const fn = tools[msg.name];
            if (fn) {
              const args = msg.arguments ? JSON.parse(msg.arguments) : {};
              const result = await fn(args);

              // Отправляем результат выполнения
              dc.send(
                JSON.stringify({
                  type: "conversation.item.create",
                  item: {
                    type: "function_call_output",
                    call_id: msg.call_id,
                    output: JSON.stringify(result),
                  },
                })
              );

              // Продолжаем диалог после выполнения
              dc.send(JSON.stringify({ type: "response.create" }));
            }
          }
        } catch (err) {
          log("Ошибка обработки DataChannel", err);
        }
      };

      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);

      const res = await fetch("https://botlegal.tech/client-offer", {
        method: "POST",
        headers: { "Content-Type": "application/sdp" },
        body: offer.sdp,
      });

      const answer = await res.text();
      await pc.setRemoteDescription({ type: "answer", sdp: answer });

      setConnected(true);
      log("Подключено к серверу и готово к вызову функций!");
    } catch (err) {
      log("Ошибка при подключении", err);
    }
  }

  function stopRelay() {
    log("🔌 Отключаюсь от сервера...");
    setConnected(false);

    try {
      if (dcRef.current) dcRef.current.close();
      if (pcRef.current) pcRef.current.close();
      if (micStreamRef.current) {
        micStreamRef.current.getTracks().forEach((track) => track.stop());
      }
      pcRef.current = null;
      dcRef.current = null;
      micStreamRef.current = null;
      log("Соединение завершено");
    } catch (err) {
      log("Ошибка при отключении", err);
    }
  }

  return (
    <div style={{ background: "#0b1020", color: "#cfe9ff", padding: 16, height: "100vh", display: 'flex', flexDirection:'column', gap:'10px'}}>
      <h2>VoiceAgent to SupervisorAgent</h2>
      <p>Статус: {connected ? "🟢 подключено" : "🔴 не подключено"}</p>

      {/* 🎛 Кнопки управления */}
      <div style={{ marginBottom: 12 }}>
        {!connected ? (
          <button
            onClick={startRelay}
            style={{
              background: "#22c55e",
              border: "none",
              padding: "8px 16px",
              borderRadius: 6,
              color: "#fff",
              cursor: "pointer",
              marginRight: 10,
            }}
          >
            Подключиться
          </button>
        ) : (
          <button
            onClick={stopRelay}
            style={{
              background: "#ef4444",
              border: "none",
              padding: "8px 16px",
              borderRadius: 6,
              color: "#fff",
              cursor: "pointer",
            }}
          >
            Отключиться
          </button>
        )}
      </div>

      <label>Сформулированный вопрос:</label>
      <textarea
        value={question}
        onChange={(e) => setQuestion(e.target.value)}
        placeholder="Здесь появится вопрос, который сформулирует ИИ"
        style={{
          width: "90vw",
          height: 80,
          background: "#1e293b",
          color: "#cfe9ff",
          padding: 10,
          borderRadius: 8,
          border: "1px solid #334155",
          resize: "none",
          fontFamily: "monospace",
          marginBottom: 10,
        }}
      />

      <label>Ответ SupervisorAgent:</label>
      <textarea
        value={answer}
        readOnly
        placeholder="Ответ появится здесь..."
        style={{
          width: "90vw",
          height: 100,
          background: "#0f172a",
          color: "#cfe9ff",
          padding: 10,
          borderRadius: 8,
          border: "1px solid #334155",
          resize: "none",
          fontFamily: "monospace",
          marginBottom: 10,
        }}
      />

      {/* <div
        style={{
          background: "#0f172a",
          padding: 12,
          borderRadius: 8,
          height: 240,
          overflowY: "auto",
          fontFamily: "monospace",
          fontSize: 12,
        }}
        {logs.map((l, i) => (
          <div key={i}>{l}</div>
        ))}
      </div> */}
    </div>
  );
}
